import { Box, Flex, HStack, Image, SimpleGrid, Spinner, Text, useDisclosure, VStack } from '@chakra-ui/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Clock, Sparkles, TriangleAlert } from 'lucide-react';

import svgFiles from '@/assets/svg/svgFiles';
import { MAX_CONTAINER_WIDTH } from '@/constants/dimensions';
import roles from '@/constants/roles';
import sharedRoutes from '@/constants/routes/shared';
import useContentAuthorProfile from '@/hooks/content/useContentAuthorProfile';
import useContentCreatorApplicationMe from '@/hooks/contentcreator/useContentCreatorApplicationMe';
import useInfiniteScrollSentinel from '@/hooks/useInfiniteScrollSentinel';
import useMyContent from '@/hooks/content/useMyContent';
import CustomButton from '@/components/atoms/CustomButton';
import ContentCard from '@/modules/content/common/ContentCard';
import { getContentDisplayStatus } from '@/modules/content/common/ContentStatusBadge';
import ContentCardSkeletonGrid from '@/modules/content/common/ContentCardSkeletonGrid';
import ContentEmptyState from '@/modules/content/common/ContentEmptyState';
import CreateContentModal from '@/modules/content/creation/CreateContentModal';
import { contentFocusOptions } from '@/modules/contentCreator/creatorSchema';
import { useAppStore } from '@/store/useAppStore';
import { useCreatorApplicationStore } from '@/store/useCreatorApplicationStore';
import { background, secondary, text } from '@/theme/colors/palettes';
import SvgIconThreeDots from '@/components/atoms/SvgIconThreeDots';
import Dropdown from '@/components/atoms/Dropdown';
import { useState } from 'react';
import { TContent } from '@/types/Content.types';

// How far the avatar sits above the sidebar card. The content column is pulled up by the
// same amount on desktop so "Your Content" lines up with the top of the photo rather than
// with the card behind it — one constant, so the two cannot drift apart.
const AVATAR_OVERHANG = '60px';

const PROFILE_CARD_SIZE = { xl: '100%', sm: '280px' } as const;

const CreatorContentCard = ({ content, onOpen }: { content: TContent; onOpen: () => void }) => (
  <Box
    role={'group'}
    cursor={'pointer'}
    borderRadius={'14px'}
    transition={'transform 0.25s ease, box-shadow 0.25s ease'}
    position={'relative'}
    onClick={onOpen}
  >
    <ContentCard
      content={content}
      size={PROFILE_CARD_SIZE}
      compact
      shouldRaiseOnHover
      status={getContentDisplayStatus(content)}
    />
  </Box>
);

// `canClaimCreator` guards the fallback: this page is reachable by applicants and by anyone
// who types the URL, and neither is a creator. Without it every visitor is labelled one.
const getRoleLabel = (userRoles: string[] = [], canClaimCreator = true) => {
  if (userRoles.includes(roles.peerSupport)) return 'Peer Support';
  if (userRoles.includes(roles.orgAdmin)) return 'Organization Admin';
  return canClaimCreator ? 'Content Creator' : undefined;
};

const CreatorProfile = () => {
  const navigate = useNavigate();
  const { user, organization } = useAppStore();
  const previewDataFromStore = useCreatorApplicationStore((state) => state.previewData);
  const authorId = user?.user_id;
  const { profile } = useContentAuthorProfile(authorId);

  // The route guard lets a submitted applicant onto this page before their role is granted,
  // so "can I see this profile" and "am I a creator" are no longer the same question. Every
  // creator-only surface below hangs off this.
  const isApprovedCreator = Boolean(
    user?.roles?.includes(roles.contentCreator) || user?.roles?.includes(roles.superAdmin),
  );

  // A pending applicant has no approved application, so /content/authors/:userId returns
  // none of their submitted profile fields — and the apply flow resets the preview store
  // before it navigates here, so that fallback is empty by the time they arrive. Read the
  // submitted application itself; it is the only place these fields still exist.
  // Only fetched for non-creators — this is the sole source of an applicant's submitted
  // profile, and approved creators get theirs from the author profile instead.
  const { data: myApplication, isLoading: isApplicationLoading } = useContentCreatorApplicationMe();
  const currentApplication = isApprovedCreator ? undefined : myApplication?.currentApplication;
  const applicationProfile = currentApplication?.profile;
  const hasApplicationInReview = currentApplication?.status === 'pending';

  // Three states, and the card must agree with the panel beside it: a creator's role, an
  // applicant's pending status, or — for someone who simply opened the URL — whatever they
  // actually are, which may be nothing worth printing.
  const roleLabel = isApprovedCreator
    ? getRoleLabel(user?.roles)
    : isApplicationLoading
      ? undefined
      : hasApplicationInReview
        ? 'Application pending'
        : getRoleLabel(user?.roles, false);

  // This route is always the signed-in creator's own profile, so read from /content/me:
  // the public author feed returns published, visibility-scoped items only, which hides
  // the drafts and queued videos a creator most needs to see here.
  const { content, totalItems, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useMyContent({ enabled: isApprovedCreator });

  const loadMoreRef = useInfiniteScrollSentinel({ hasNextPage: !!hasNextPage, isFetchingNextPage, fetchNextPage });

  const metadata = user?.user_metadata;
  const realName =
    [metadata?.firstName ?? user?.firstName, metadata?.lastName ?? user?.lastName].filter(Boolean).join(' ').trim() ||
    user?.name ||
    'Creator';
  const name = isApprovedCreator
    ? profile?.creatorDisplayName?.trim() || profile?.name || 'Creator'
    : applicationProfile?.keepNameSameAsRealName === false
      ? applicationProfile.creatorName?.trim() || 'Creator'
      : realName;
  // The author profile is refetched after edits; account metadata can still be stale.
  // Keep pending applicants on their submitted location until their profile is approved.
  const profileLocation = isApprovedCreator
    ? (profile?.location ?? metadata)
    : (applicationProfile?.location ?? profile?.location ?? metadata);
  const location = [profileLocation?.city, profileLocation?.state]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ');
  const profilePicture = profile?.photoUrl || applicationProfile?.photoUrl || svgFiles.genericUser;
  const aboutMe = profile?.headline || applicationProfile?.bio || metadata?.bio;
  const qualifications =
    profile?.certifications ?? applicationProfile?.certifications ?? previewDataFromStore?.qualifications ?? [];
  const contentFocus = (
    profile?.contentFocus ??
    applicationProfile?.contentFocus ??
    previewDataFromStore?.contentFocus ??
    []
  )
    .map((value) => contentFocusOptions.find((option) => option.value === value)?.label ?? value)
    .join(', ');

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isThreeDotsHovered, setIsThreeDotsHovered] = useState(false);
  const profileDropdownDisclosure = useDisclosure();
  return (
    <Flex
      width={MAX_CONTAINER_WIDTH}
      gap={'20px'}
      mt={'60px'}
      alignItems={'flex-start'}
      direction={{ base: 'column', lg: 'row' }}
      paddingBottom={'48px'}
    >
      <VStack
        width={'295px'}
        flexShrink={0}
        backgroundColor={background.lightGray}
        borderRadius={'20px'}
        padding={'20px'}
        paddingTop={'40px'}
        boxShadow={'0px 10px 80px 0px #00417926'}
        alignItems={'flex-start'}
        gap={'16px'}
        pos={'relative'}
      >
        <Box
          position={'absolute'}
          top={`-${AVATAR_OVERHANG}`}
          left={'20px'}
          width={'120px'}
          height={'120px'}
          borderRadius={'full'}
          overflow={'hidden'}
          border={'4px solid white'}
          backgroundColor={'white'}
          boxShadow={'0px 8px 28px rgba(14, 0, 98, 0.18)'}
          alignSelf={{ base: 'center', lg: 'flex-start' }}
        >
          <Image src={profilePicture} alt={name} width={'100%'} height={'100%'} objectFit={'cover'} />
        </Box>

        {isApprovedCreator && (
          <Dropdown
            disclosure={profileDropdownDisclosure}
            title={'Creator profile actions'}
            options={['Edit Profile']}
            optionLabelDisplay={(option) => option}
            width={150}
            popoverWidth={150}
            onOptionClick={() => {
              profileDropdownDisclosure.onClose();
              navigate({ to: '/creator/profile/edit' });
            }}
            trigger={
              <Box
                position={'absolute'}
                width={'30px'}
                height={'30px'}
                top={'20px'}
                right={'10px'}
                paddingLeft={'10px'}
                cursor={'pointer'}
                onMouseEnter={() => setIsThreeDotsHovered(true)}
                onMouseLeave={() => setIsThreeDotsHovered(false)}
                aria-label={'Open creator profile menu'}
              >
                <SvgIconThreeDots
                  color={isThreeDotsHovered || profileDropdownDisclosure.isOpen ? secondary[500] : text.mediumBlue}
                  size={20}
                />
              </Box>
            }
          />
        )}

        <VStack alignItems={'flex-start'} gap={'7px'} width={'100%'} pt={'40px'}>
          <Text
            variant={'loraMediumTitle'}
            fontWeight={400}
            color={text.mediumBlue}
            fontSize={'28px'}
            lineHeight={'34px'}
          >
            {name}
          </Text>
          <Text color={'text.mediumBlue'} fontSize={'16px'} fontWeight={600}>
            {[roleLabel, organization?.name].filter(Boolean).join(' | ')}
          </Text>
        </VStack>

        <HStack gap={'18px'} flexWrap={'wrap'}>
          <HStack gap={'6px'}>
            <Image src={svgFiles.play} alt="" width={'16px'} height={'16px'} />
            <Text color={secondary[500]} fontWeight={800} fontSize={'12px'} textTransform={'uppercase'}>
              {totalItems} Content
            </Text>
          </HStack>
        </HStack>

        {aboutMe && (
          <VStack alignItems={'flex-start'} gap={'0px'}>
            <Text color={'text.mediumBlue'} fontFamily={'Urbanist'} fontWeight={800} fontSize={'16px'}>
              About Me
            </Text>
            <Text
              color={'text.mediumBlue'}
              fontFamily={'Urbanist'}
              fontSize={'16px'}
              fontWeight={500}
              lineHeight={'18px'}
              whiteSpace={'pre-wrap'}
              maxWidth={'250px'}
            >
              {aboutMe}
            </Text>
          </VStack>
        )}

        {location && (
          <VStack alignItems={'flex-start'} gap={'0px'}>
            <Text color={'text.mediumBlue'} fontWeight={800} fontSize={'16px'}>
              Location
            </Text>
            <Text color={'text.mediumBlue'} fontSize={'16px'} lineHeight={'18px'}>
              {location}
            </Text>
          </VStack>
        )}

        {qualifications.length > 0 && (
          <VStack alignItems={'flex-start'} gap={'0px'}>
            <Text color={'text.mediumBlue'} fontFamily={'Urbanist'} fontWeight={800} fontSize={'16px'}>
              Qualifications or Certifications
            </Text>
            <Text
              color={'text.mediumBlue'}
              fontFamily={'Urbanist'}
              fontSize={'16px'}
              fontWeight={500}
              lineHeight={'18px'}
            >
              {qualifications.join(', ')}
            </Text>
          </VStack>
        )}

        {contentFocus && (
          <VStack alignItems={'flex-start'} gap={'0px'}>
            <Text color={'text.mediumBlue'} fontFamily={'Urbanist'} fontWeight={800} fontSize={'16px'}>
              Content Focus
            </Text>
            <Text
              color={'text.mediumBlue'}
              fontFamily={'Urbanist'}
              fontSize={'16px'}
              fontWeight={500}
              lineHeight={'18px'}
            >
              {contentFocus}
            </Text>
          </VStack>
        )}
      </VStack>

      <VStack
        flex={1}
        minWidth={0}
        width={'100%'}
        alignItems={'stretch'}
        gap={'20px'}
        // Only in the side-by-side layout; stacked on narrow screens the card is above and
        // there is nothing to align to.
        marginTop={{ base: '0', lg: `-${AVATAR_OVERHANG}` }}
      >
        {!isApprovedCreator ? (
          // A submitted applicant can reach this page before their role is granted. Creating
          // and listing content both need that role, so show them where they actually are
          // rather than a content grid that cannot load.
          //
          // The guard admits anyone on this exact path, so someone who never applied can open
          // it by typing the URL. Nothing links them here, but telling them an application is
          // under review when they have not submitted one would simply be untrue.
          isApplicationLoading ? (
            // Whether they applied is not known yet, and they arrive here straight from
            // submitting — so guessing "not applied" would flash the wrong invitation at the
            // exact person who just finished applying.
            <HStack width={'100%'} justifyContent={'center'} paddingY={'48px'}>
              <Spinner color={'primary.500'} size={'lg'} />
            </HStack>
          ) : hasApplicationInReview ? (
            <ContentEmptyState
              icon={Clock}
              title="Your application is under review"
              description="We’ll email you when it’s approved. You’ll be able to create and publish content from here once it is."
            />
          ) : (
            <ContentEmptyState
              icon={Sparkles}
              title="This is where your creator profile will live"
              description="Apply to become a content creator and you’ll be able to write, publish and manage everything from here."
              actionLabel="Apply to create"
              onAction={() => navigate({ to: sharedRoutes.creatorApply })}
            />
          )
        ) : (
          <>
            {/* Only heads an actual list. With nothing to show, the empty state carries its
                own Create action, so this row would just repeat it above a pictogram — and
                while loading or on an error there is nothing to head either. */}
            {content.length > 0 && (
              <HStack width={'100%'} justifyContent={'space-between'} alignItems={'center'} gap={'16px'}>
                <Text variant={'loraMediumTitle'} color={'text.darkBlue'}>
                  Your Content
                </Text>

                <CustomButton
                  label={'Create New Content'}
                  backgroundColor={'primary.500'}
                  labelColor={'extra.white'}
                  width={'240px'}
                  onClick={() => setIsCreateModalOpen(true)}
                />
              </HStack>
            )}

            {!authorId ? (
              <ContentEmptyState
                icon={TriangleAlert}
                title="Couldn’t identify your creator profile"
                description="Please sign in again and retry."
              />
            ) : isLoading ? (
              <ContentCardSkeletonGrid count={6} cardSize={PROFILE_CARD_SIZE} />
            ) : isError ? (
              <ContentEmptyState
                icon={TriangleAlert}
                title="Couldn’t load your content"
                description="Something went wrong while loading your profile. Please try again."
                actionLabel="Try again"
                onAction={() => refetch()}
              />
            ) : content.length === 0 ? (
              <ContentEmptyState
                icon={Sparkles}
                title="No content yet"
                description="Content you create will appear here, drafts included."
                actionLabel="Create New Content"
                onAction={() => setIsCreateModalOpen(true)}
              />
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap={'20px'} paddingX={'12px'} alignItems={'start'}>
                {content.map((item) => (
                  <CreatorContentCard
                    key={item.id}
                    content={item}
                    onOpen={() =>
                      navigate({
                        to: '/loop/$contentId',
                        params: { contentId: item.id },
                        search: { source: 'library' },
                      })
                    }
                  />
                ))}
              </SimpleGrid>
            )}

            {/* Infinite-scroll sentinel — matches the explore feed. */}
            {content.length > 0 && hasNextPage && (
              <Box ref={loadMoreRef} width={'100%'}>
                {isFetchingNextPage && (
                  <HStack width={'100%'} justifyContent={'center'} paddingY={'24px'}>
                    <Spinner color={'primary.500'} size={'md'} />
                  </HStack>
                )}
              </Box>
            )}
          </>
        )}
      </VStack>

      <CreateContentModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} />
    </Flex>
  );
};

export const Route = createFileRoute('/_authenticated/_creator/creator/profile/')({
  staticData: { title: 'Creator Profile' },
  component: CreatorProfile,
});
